import { ContributorRole, DRMFeaturesConfig, IAuthor, IBook, IBookContent, IBookDetails, IGenre, IReview, ISeries } from '@pressreader/catalog-types';

import { IBookContentDto, IBookDetailsDto, IBookDto, ICategoryDto, IContributorDto, IGenreDto, IReviewDto, ISeriesDto } from './book.dto';

function mapBookFromDto(dto: IBookDto): IBook {
    return {
        id: dto.id,
        prn: dto.prn,
        isbn: dto.isbn,
        title: dto.title,
        subtitle: dto.subtitle,
        description: dto.description,
        language: dto.language,
        language2: dto.language2,
        pageCount: dto.pageCount,
        imageId: dto.cover?.imageId,
        authors: dto.contributors.filter(contributor => (contributor.roles || []).indexOf(ContributorRole.AUTHOR) >= 0).map(mapAuthorFromDto),
        publicationDate: dto.publicationDate ? new Date(dto.publicationDate) : null,
        publisher: dto.publisher.name,
        series: dto.series ? dto.series.map(mapSeriesFromDto) : [],
        sample: dto.sample ? mapBookContentFromDto(dto.sample) : undefined,
        lastOpenedAs: 'none',
        slug: dto.slug,
        isFree: dto.isFree,
        isSponsored: dto.isSponsored,
        supplier: dto.supplier.name,
    };
}

const mapDRMContentCopyingFeatureConfigFromDTO = (dto: IBookDetailsDto): DRMFeaturesConfig['contentCopying'] => {
    if (typeof dto.drm.copy !== 'boolean') {
        return;
    }
    return {
        enabled: dto.drm.copy === false, // suppressing of the text selection feature is disabled if copy is not allowed
        numberOfCharacters: dto.drm.copyCharacterLimit ?? void undefined,
    };
};

const mapDRMPrintingFeatureConfigFromDTO = (dto: IBookDetailsDto): DRMFeaturesConfig['print'] => {
    if (typeof dto.drm.print !== 'boolean') {
        return;
    }
    return {
        enabled: dto.drm.print === false, // suppressing of the printing feature is enabled if print is not allowed
    };
};

const mapDRMFeaturesConfigFromDTO = (dto: IBookDetailsDto): DRMFeaturesConfig | null => {
    const configObject: DRMFeaturesConfig = {};
    const printFeatureConfig = mapDRMPrintingFeatureConfigFromDTO(dto);
    const contentCopyingFeatureConfig = mapDRMContentCopyingFeatureConfigFromDTO(dto);
    const isConfigEmpty = !printFeatureConfig && !contentCopyingFeatureConfig;

    if (isConfigEmpty) {
        return null;
    }

    if (printFeatureConfig) {
        configObject.print = printFeatureConfig;
    }
    if (contentCopyingFeatureConfig) {
        configObject.contentCopying = contentCopyingFeatureConfig;
    }
    return configObject;
};

function mapBookDetailsFromDto(dto: IBookDetailsDto): IBookDetails {
    const mappedCategories = dto.categories ? dto.categories.map(mapGenreFromDto) : [];
    return {
        ...mapBookFromDto(dto),
        genres: mappedCategories.reduce((acc, item) => {
            if (item) {
                acc.push(item);
            }
            return acc;
        }, [] as IGenre[]),
        biographicalNotes: dto.biographicalNotes,
        reviews: dto.reviews ? dto.reviews.map(mapReviewFromDto) : [],
        content: dto.content ? mapBookContentFromDto(dto.content) : undefined,
        drm: mapDRMFeaturesConfigFromDTO(dto),
    };
}

function mapAuthorFromDto(dto: IContributorDto): IAuthor {
    return {
        id: dto.id,
        name: dto.name,
        slug: dto.slug,
    };
}

function mapReviewFromDto(dto: IReviewDto): IReview {
    return {
        author: dto.author,
        text: dto.text,
    };
}

function mapSeriesFromDto(dto: ISeriesDto): ISeries {
    return {
        id: dto.id,
        title: dto.title,
        subtitle: dto.subtitle,
        slug: dto.slug,
        number: dto.number,
    };
}

function mapBookContentFromDto(dto: IBookContentDto): IBookContent {
    return { contentId: dto.contentId, contentType: dto.contentType, size: dto.size };
}

function mapGenreFromCategoryDto(dto: ICategoryDto): IGenre {
    const parent = dto.data.parent ? mapGenreFromDto(dto.data.parent) : null;
    const ancestors = dto.data.ancestors ? [...dto.data.ancestors] : [];
    return {
        id: dto.data.id,
        displayName: dto.data.name,
        slug: dto.data.slug,
        isFeatured: dto.data.isFeatured,
        parentId: dto.data.parentId,
        path: dto.data.path,
        sortOrder: dto.data.sortOrder,
        parent,
        ancestors,
    };
}

function mapGenreFromDto(dto?: IGenreDto): IGenre | null {
    if (!dto) {
        return null;
    }
    const { id, name, slug, isFeatured, parentId, path, sortOrder, parent: parentDto, ancestors: ancestorsDto } = dto;

    const parent = parentDto ? mapGenreFromDto(parentDto) : null;
    const ancestors = ancestorsDto ?? [];

    return {
        id,
        displayName: name,
        slug,
        isFeatured,
        parentId,
        path,
        sortOrder,
        parent,
        ancestors,
    };
}

export { mapAuthorFromDto, mapBookDetailsFromDto, mapBookFromDto, mapGenreFromCategoryDto, mapGenreFromDto, mapSeriesFromDto };
